import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  describeMessagePreview,
  isChatAdminIdentity,
  isClownProfile,
  isHelperModeratorProfile,
  isMessageDeleted,
  isModeratorProfile,
  isProfileTimedOut,
  normalizeLoginName,
  tsMs,
  xpLevelInfoForProfile,
  xpPointsForProfile,
  xpRewardForProfile,
} from "./chatService";
import { avatarColor, avatarInitial } from "./avatar";
import EmojiPickerPopover from "./EmojiPickerPopover";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🔥"];

function fmtTime(ts) {
  const ms = tsMs(ts);
  if (!ms) return "…";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayKey(ts) {
  const ms = tsMs(ts);
  if (!ms) return "pending";
  return new Date(ms).toDateString();
}

function dayLabel(ts) {
  const ms = tsMs(ts);
  if (!ms) return "Sending";
  const d = new Date(ms);
  const today = new Date(); today.setHours(0,0,0,0);
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const dDay = new Date(d); dDay.setHours(0,0,0,0);
  if (dDay.getTime() === today.getTime()) return "Today";
  if (dDay.getTime() === yest.getTime()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

function decryptReasonLabel(rawError) {
  const text = String(rawError || "").toLowerCase();
  if (!text) return "Decrypt failed: message keys unavailable.";
  if (text.includes("missing sender key packet")) {
    return "Decrypt failed: sender key packet missing for your account.";
  }
  if (text.includes("missing sender public key") || text.includes("missing peer public key")) {
    return "Decrypt failed: public key snapshot missing.";
  }
  if (text.includes("operation-specific reason") || text.includes("decrypt")) {
    return "Decrypt failed: key mismatch or stale local key cache.";
  }
  return "Decrypt failed: message keys unavailable.";
}

function imageAltText(message) {
  const caption = String(message.body || "").trim();
  return caption ? `Shared image: ${caption.slice(0, 80)}` : "Shared image";
}

function ChatMessageImage({ dataUrl, alt }) {
  const [decodedUrl, setDecodedUrl] = useState("");

  useEffect(() => {
    if (!dataUrl) {
      setDecodedUrl("");
      return undefined;
    }
    if (!dataUrl.startsWith("data:")) {
      setDecodedUrl(dataUrl);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = "";

    fetch(dataUrl, { signal: controller.signal })
      .then((response) => response.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setDecodedUrl(objectUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setDecodedUrl(dataUrl);
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dataUrl]);

  if (!dataUrl) return null;

  const imageSrc = decodedUrl || dataUrl;

  return (
    <a
      className="chat-msg-image-card"
      href={imageSrc}
      target="_blank"
      rel="noreferrer"
      title="Open image"
    >
      <img
        className="chat-msg-image"
        src={imageSrc}
        alt={alt}
        loading="lazy"
      />
    </a>
  );
}

export default function ChatMessages({
  messages,
  meUid,
  profilesByUid,
  memberIds = [],
  typingIndicatorText = "",
  readReceipts = {},
  searchResultIds = [],
  activeSearchMessageId = "",
  onReply,
  onForward,
  onReact,
  onEdit,
  onDelete,
  onTimeoutUser,
  onClearTimeout,
  canModerate = false,
  moderationQuota = null,
  moderationLimits = null,
  isAdmin = false,
  interactionDisabled = false,
}) {
  const scrollerRef = useRef(null);
  const lastCountRef = useRef(0);
  const [showJump, setShowJump] = useState(false);
  const [pickerMessageId, setPickerMessageId] = useState(null);
  const [deleteChoiceMessageId, setDeleteChoiceMessageId] = useState(null);
  const [timeoutChoiceMessageId, setTimeoutChoiceMessageId] = useState(null);
  const [customTimeoutMinutes, setCustomTimeoutMinutes] = useState("30");
  const searchResultSet = useMemo(() => new Set(searchResultIds), [searchResultIds]);

  const profilesByLoginName = useMemo(() => {
    const out = {};
    Object.values(profilesByUid || {}).forEach((profile) => {
      const handle = normalizeLoginName(profile?.loginName || profile?.displayName || "");
      if (handle) out[handle] = profile;
    });
    return out;
  }, [profilesByUid]);

  const messagesById = useMemo(() => {
    const out = {};
    (messages || []).forEach((message) => {
      if (!message?.id) return;
      out[message.id] = message;
    });
    return out;
  }, [messages]);

  // Build a flat list with day separators + sender grouping (same sender within 4 min collapses).
  const items = useMemo(() => {
    const out = [];
    let lastDay = null;
    let lastSender = null;
    let lastTs = 0;
    for (const m of messages) {
      const dKey = dayKey(m.createdAt);
      if (dKey !== lastDay) {
        out.push({ kind: "day", id: "d-" + dKey, label: dayLabel(m.createdAt) });
        lastDay = dKey;
        lastSender = null;
        lastTs = 0;
      }
      const ms = tsMs(m.createdAt);
      const grouped = m.senderId === lastSender && ms && lastTs && ms - lastTs < 4 * 60_000;
      out.push({ kind: "msg", id: m.id, message: m, grouped });
      lastSender = m.senderId;
      if (ms) lastTs = ms;
    }
    return out;
  }, [messages]);

  // Always keep the newest message in view when the list grows.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const grew = items.length > lastCountRef.current;
    if (grew) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: lastCountRef.current === 0 ? "auto" : "smooth",
      });
      setShowJump(false);
    }
    lastCountRef.current = items.length;
  }, [items.length]);

  useLayoutEffect(() => {
    if (!typingIndicatorText) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: "smooth",
    });
    setShowJump(false);
  }, [typingIndicatorText]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    setPickerMessageId(null);
    setDeleteChoiceMessageId(null);
    setTimeoutChoiceMessageId(null);
  }, [messages]);

  useEffect(() => {
    if (!activeSearchMessageId) return;
    const row = scrollerRef.current?.querySelector(`[data-message-id="${activeSearchMessageId}"]`);
    if (!row) return;
    row.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeSearchMessageId]);

  function handleDeleteSelection(message, scope) {
    setDeleteChoiceMessageId(null);
    onDelete && onDelete(message, scope);
  }

  function handleTimeoutSelection(targetUid, minutes) {
    const safeMinutes = Number(minutes || 0);
    if (!Number.isFinite(safeMinutes) || safeMinutes <= 0) return;
    setTimeoutChoiceMessageId(null);
    onTimeoutUser && onTimeoutUser(targetUid, safeMinutes * 60 * 1000);
  }

  function onScroll() {
    const el = scrollerRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (dist < 80) setShowJump(false);
  }

  function jumpToBottom() {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setShowJump(false);
  }

  return (
    <div className="chat-msg-wrap">
      <div className="chat-msg-list" ref={scrollerRef} onScroll={onScroll}>
        {items.length === 0 && (
          <div className="chat-msg-empty">No messages yet — say hi 👋</div>
        )}
        {items.map((it) => {
          if (it.kind === "day") {
            return (
              <div key={it.id} className="chat-msg-day">
                <span>{it.label}</span>
              </div>
            );
          }
          const m = it.message;
          const isOwn = m.senderId === meUid;
          const sender = profilesByUid[m.senderId];
          const senderProfile = sender || profilesByUid[meUid] || {};
          const senderName = isOwn ? "You" : (sender ? sender.displayName : "Unknown");
          const senderXpLevel = xpLevelInfoForProfile(senderProfile);
          const senderXpReward = xpRewardForProfile(senderProfile);
          const senderXpPoints = xpPointsForProfile(senderProfile);
          const pending = !m.createdAt || (m.createdAt && !tsMs(m.createdAt));
          const deleted = isMessageDeleted(m);
          const canEdit = isOwn && !deleted;
          const canDeleteForEveryone = isOwn && !deleted;
          const senderIsAdmin = isChatAdminIdentity(sender || {});
          const senderIsHelperModerator = !senderIsAdmin && isHelperModeratorProfile(sender || {});
          const senderIsModerator = !senderIsAdmin && isModeratorProfile(sender || {});
          const senderIsClown = !senderIsAdmin && isClownProfile(sender || {});
          const senderTimedOut = isProfileTimedOut(sender);
          const canModerateSender = canModerate && !isOwn && !senderIsAdmin;
          const readBy = readByForMessage(m, meUid, memberIds, readReceipts);
          const forwardedSender = m.forwardedFrom?.senderId
            ? profilesByUid[m.forwardedFrom.senderId]
            : null;
          const forwardedSenderName = m.forwardedFrom
            ? (m.forwardedFrom.senderId === meUid
                ? "You"
                : (forwardedSender?.displayName || "Someone"))
            : "";
          const replySource = m.replyTo?.id
            ? (messagesById[m.replyTo.id] || m.replyTo)
            : m.replyTo;
          const replySenderId = m.replyTo?.senderId || replySource?.senderId || "";
          const rawReplyPreview = String(m.replyTo?.body || "").trim();
          let replyPreview = rawReplyPreview;
          if (!replyPreview || /^encrypted reply$/i.test(replyPreview)) {
            replyPreview = describeMessagePreview(replySource || {}) || replyPreview || "Message";
          }
          if (/^\[unable to decrypt message\]$/i.test(replyPreview)) {
            replyPreview = "Original message unavailable";
          }
          const unresolvedDecrypt = /^\[unable to decrypt message\]$/i.test(String(m.body || ""));
          const decryptReason = unresolvedDecrypt
            ? decryptReasonLabel(m.decryptError)
            : "";

          return (
            <div
              key={m.id}
              data-message-id={m.id}
              className={
                "chat-msg-row"
                + (isOwn ? " own" : "")
                + (it.grouped ? " grouped" : "")
                + (pending ? " pending" : "")
                + (searchResultSet.has(m.id) ? " search-hit" : "")
                + (activeSearchMessageId === m.id ? " search-active" : "")
              }
              title={tsMs(m.createdAt) ? new Date(tsMs(m.createdAt)).toLocaleString() : "Sending…"}
            >
              <div className={"chat-msg-avatar-cell" + (it.grouped ? " spacer" : "")}>
                {it.grouped ? (
                  <span className="chat-msg-hover-time">{fmtTime(m.createdAt)}</span>
                ) : (
                  <div
                    className="chat-avatar"
                    style={{ background: avatarColor(m.senderId) }}
                    aria-hidden="true"
                  >
                    {avatarInitial(senderName)}
                  </div>
                )}
              </div>
              <div className="chat-msg-content">
                {!it.grouped && (
                  <div className="chat-msg-meta">
                    <span className="chat-msg-author">{senderName}</span>
                    <span
                      className={`chat-xp-badge ${senderXpReward.key}`}
                      title={`${senderXpPoints} XP • Level ${senderXpLevel.level}`}
                    >
                      {senderXpReward.label} L{senderXpLevel.level}
                    </span>
                    {senderIsAdmin && <span className="chat-role-badge admin">Admin Vivaan</span>}
                    {senderIsModerator && (
                      <span className={"chat-role-badge " + (senderIsHelperModerator ? "helper-moderator" : "moderator")}>
                        {senderIsHelperModerator ? "Helper mod" : "Respected moderator"}
                      </span>
                    )}
                    {senderIsClown && <span className="chat-role-badge clown">Clown</span>}
                    <span className="chat-msg-time">{fmtTime(m.createdAt)}</span>
                    {m.editedAt && !deleted && <span className="chat-msg-edited">edited</span>}
                    {senderTimedOut && <span className="chat-msg-timeout-pill">timed out</span>}
                  </div>
                )}
                {m.replyTo && (
                  <button
                    type="button"
                    className="chat-msg-reply-card"
                    onClick={() => onReply && onReply(replySource || m.replyTo)}
                    title="Reply reference"
                  >
                    <span>{profilesByUid[replySenderId]?.displayName || "Message"}</span>
                    <p>{replyPreview}</p>
                  </button>
                )}
                {m.forwardedFrom && (
                  <div className="chat-msg-forward-card">
                    <span>Forwarded from {forwardedSenderName}</span>
                    <p>{m.forwardedFrom.conversationName || "Another chat"}</p>
                  </div>
                )}
                {deleted ? (
                  <div className="chat-msg-deleted">Message deleted</div>
                ) : (
                  <>
                    {m.imageDataUrl && (
                      <ChatMessageImage dataUrl={m.imageDataUrl} alt={imageAltText(m)} />
                    )}
                    {m.body && (
                      <div className="chat-msg-body">{renderMessageBody(m.body, profilesByLoginName)}</div>
                    )}
                    {unresolvedDecrypt && (
                      <div
                        className="chat-msg-decrypt-reason"
                        title={String(m.decryptError || "")}
                      >
                        {decryptReason}
                      </div>
                    )}
                    {!interactionDisabled && (
                      <div className="chat-msg-actions" aria-label="Message actions">
                      {QUICK_REACTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="chat-msg-action-btn"
                          onClick={() => onReact && onReact(m, emoji)}
                          title={`React ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                      <div className="chat-msg-reaction-picker-anchor">
                        <button
                          type="button"
                          className="chat-msg-action-btn"
                          onClick={() => setPickerMessageId((cur) => (cur === m.id ? null : m.id))}
                          title="More reactions"
                          aria-label="More reactions"
                          aria-expanded={pickerMessageId === m.id}
                        >
                          +
                        </button>
                        <EmojiPickerPopover
                          open={pickerMessageId === m.id}
                          onClose={() => setPickerMessageId((cur) => (cur === m.id ? null : cur))}
                          onPick={(emoji) => onReact && onReact(m, emoji)}
                          className="chat-emoji-popover-reaction"
                          height={340}
                        />
                      </div>
                      <button
                        type="button"
                        className="chat-msg-action-btn"
                        onClick={() => onForward && onForward(m)}
                        title="Forward"
                      >
                        Forward
                      </button>
                      <button
                        type="button"
                        className="chat-msg-action-btn"
                        onClick={() => onReply && onReply(m)}
                        title="Reply"
                      >
                        Reply
                      </button>
                      {canModerateSender && (
                        <button
                          type="button"
                          className="chat-msg-action-btn"
                          onClick={() => setTimeoutChoiceMessageId((cur) => (cur === m.id ? null : m.id))}
                          title="Timeout user"
                        >
                          Timeout
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          className="chat-msg-action-btn"
                          onClick={() => onEdit && onEdit(m)}
                          title="Edit"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        type="button"
                        className="chat-msg-action-btn chat-msg-action-danger"
                        onClick={() => setDeleteChoiceMessageId((cur) => (cur === m.id ? null : m.id))}
                        title="Delete"
                      >
                        Delete
                      </button>
                      </div>
                    )}
                    {timeoutChoiceMessageId === m.id && canModerateSender && (
                      <div className="chat-msg-timeout-choices" role="dialog" aria-label="Timeout options">
                        <span>
                          {senderName} {senderTimedOut ? `is timed out until ${formatTimeoutUntil(sender?.timeoutUntil)}` : "is not timed out."}
                        </span>
                        {!isAdmin && (
                          <small className="chat-msg-timeout-limit-note">
                            Daily limits: {Math.max(0, Number(moderationLimits?.timeoutLimit || 0) - Number(moderationQuota?.timeoutUsed || 0))} timeouts left, {Math.max(0, Number(moderationLimits?.removeLimit || 0) - Number(moderationQuota?.removeUsed || 0))} removals left.
                          </small>
                        )}
                        <div className="chat-msg-timeout-presets">
                          {[5, 15, 60, 240, 1440].map((minutes) => (
                            <button
                              key={minutes}
                              type="button"
                              className="chat-msg-action-btn"
                              onClick={() => handleTimeoutSelection(m.senderId, minutes)}
                            >
                              {formatDurationLabel(minutes)}
                            </button>
                          ))}
                        </div>
                        <div className="chat-msg-timeout-custom">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={customTimeoutMinutes}
                            onChange={(e) => setCustomTimeoutMinutes(e.target.value)}
                            placeholder="Minutes"
                          />
                          <button
                            type="button"
                            className="chat-msg-action-btn"
                            onClick={() => handleTimeoutSelection(m.senderId, customTimeoutMinutes)}
                          >
                            Apply minutes
                          </button>
                          {senderTimedOut && (
                            <button
                              type="button"
                              className="chat-msg-action-btn"
                              onClick={() => {
                                setTimeoutChoiceMessageId(null);
                                onClearTimeout && onClearTimeout(m.senderId);
                              }}
                            >
                              Clear timeout
                            </button>
                          )}
                          <button
                            type="button"
                            className="chat-msg-action-btn"
                            onClick={() => setTimeoutChoiceMessageId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {deleteChoiceMessageId === m.id && (
                      <div className="chat-msg-delete-choices" role="dialog" aria-label="Delete message options">
                        <span>Delete this message?</span>
                        <div>
                          <button
                            type="button"
                            className="chat-msg-action-btn"
                            onClick={() => handleDeleteSelection(m, "me")}
                          >
                            Delete for me
                          </button>
                          {canDeleteForEveryone && (
                            <button
                              type="button"
                              className="chat-msg-action-btn chat-msg-action-danger"
                              onClick={() => handleDeleteSelection(m, "everyone")}
                            >
                              Delete for everyone
                            </button>
                          )}
                          <button
                            type="button"
                            className="chat-msg-action-btn"
                            onClick={() => setDeleteChoiceMessageId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    <ReactionChips
                      reactions={m.reactions}
                      meUid={meUid}
                      onPick={interactionDisabled ? null : (emoji) => onReact && onReact(m, emoji)}
                    />
                  </>
                )}
                {m.senderId === meUid && !deleted && readBy.length > 0 && (
                  <div className="chat-read-receipt">
                    Seen by {readBy.map((uid) => profilesByUid[uid]?.displayName || "Someone").join(", ")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showJump && (
        <button className="chat-jump-pill" onClick={jumpToBottom}>↓ New messages</button>
      )}
    </div>
  );
}

function renderMessageBody(body, profilesByLoginName) {
  const text = String(body || "");
  const parts = [];
  const regex = /@([a-z0-9]+)/gi;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text))) {
    const handle = normalizeLoginName(match[1]);
    const isGlobalMention = handle === "everyone" || handle === "here";
    const profile = profilesByLoginName[handle];
    if (!profile && !isGlobalMention) continue;

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={`${handle}-${match.index}`}
        className={"chat-mention" + (isGlobalMention ? " chat-mention-global" : "")}
      >
        @{handle}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (parts.length === 0) return text;
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function ReactionChips({ reactions, meUid, onPick }) {
  const entries = Object.entries(reactions || {})
    .map(([emoji, users]) => [emoji, Array.isArray(users) ? users : []])
    .filter(([, users]) => users.length > 0);
  if (!entries.length) return null;
  return (
    <div className="chat-reaction-chips">
      {entries.map(([emoji, users]) => (
        <button
          key={emoji}
          type="button"
          className={users.includes(meUid) ? "active" : ""}
          onClick={() => onPick && onPick(emoji)}
          title={`${users.length} reaction${users.length === 1 ? "" : "s"}`}
          disabled={!onPick}
        >
          <span>{emoji}</span>
          <strong>{users.length}</strong>
        </button>
      ))}
    </div>
  );
}

function formatDurationLabel(minutes) {
  if (minutes % 1440 === 0) return `${minutes / 1440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}m`;
}

function formatTimeoutUntil(timeoutUntil) {
  const ms = tsMs(timeoutUntil);
  if (!ms) return "later";
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function readByForMessage(message, meUid, memberIds, readReceipts) {
  const messageMs = tsMs(message.createdAt);
  if (!messageMs || message.senderId !== meUid) return [];
  return memberIds.filter((uid) => {
    if (uid === meUid) return false;
    const readMs = tsMs(readReceipts[uid]?.updatedAt);
    return readMs && readMs >= messageMs;
  });
}
